import React, { useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import "./index.css";
import ClipLoader from "react-spinners/ClipLoader";
import { useIsFetching, useIsMutating } from "@tanstack/react-query";
import {
	KBarProvider,
	KBarPortal,
	KBarPositioner,
	KBarResults,
	useMatches,
	KBarAnimator,
	KBarSearch,
} from "kbar";
import { Toaster } from "./components/ui-components/Toast";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { ConfigProvider as AntdConfigProvider } from "antd";
import muiTheme from "./theme/muiTheme";
import antdTheme from "./theme/antdTheme";

const override = {
	display: "block",
	top: "50%",
	left: "50%",
	position: "absolute",
	zIndex: 1000,
};

export default function App(props) {
	// variables for organization selection (keeping these in context for now)
	const [selectedOrganisation, setSelectedOrganisation] = useState("");
	const [selectedOrganisationLevel, setSelectedOrganisationLevel] =
		useState(null);

	const location = useLocation();
	
	// Check if we're in print mode (for PDF generation)
	const searchParams = new URLSearchParams(location.search);
	const isPrintMode = searchParams.get('print') === 'true';

	// Exclude organisations query from global loading indicator.
	// On hierarchy page, also exclude cost-model-nodes/edges so only the in-node spinner shows during move.
	const isFetching = useIsFetching({
		predicate: (query) => {
			if (!query.queryKey || !Array.isArray(query.queryKey)) return true;
			const key0 = query.queryKey[0];
			if (key0 === 'organisations') return false;
			if (location.pathname === '/cost-allocation/hierarchy' && (key0 === 'cost-model-nodes' || key0 === 'cost-model-edges')) {
				return false;
			}
			return true;
		}
	});
	const isMutating = useIsMutating();
	const actions = [
		{
			id: "blog",
			name: "Blog",
			shortcut: ["b"],
			keywords: "writing words",
			perform: () => (window.location.pathname = "blog"),
		},
		{
			id: "contact",
			name: "Contact",
			shortcut: ["c"],
			keywords: "email",
			perform: () => (window.location.pathname = "contact"),
		},
		{
			id: "blog1",
			name: "Blog",
			shortcut: ["b"],
			keywords: "writing words",
			perform: () => (window.location.pathname = "blog"),
		},
		{
			id: "contact1",
			name: "Contact",
			shortcut: ["c"],
			keywords: "email",
			perform: () => (window.location.pathname = "contact"),
		},
		{
			id: "blog2",
			name: "Blog",
			shortcut: ["b"],
			keywords: "writing words",
			perform: () => (window.location.pathname = "blog"),
		},
		{
			id: "contact2",
			name: "Contact",
			shortcut: ["c"],
			keywords: "email",
			perform: () => (window.location.pathname = "contact"),
		},
	];

	function RenderResults() {
		const { results } = useMatches();

		return (
			<KBarResults
				items={results}
				className="max-h-72 overflow-y-auto flex flex-col"
				onRender={({ item, active }) =>
					typeof item === "string" ? (
						<div className="flex items-center h-6 flex-shrink-0 bg-accent/50">
							<span className="text-xs text-slate-100 px-3.5">{item}</span>
						</div>
					) : (
						<div
							className={`px-3.5 w-full h-[46px] text-text-2 flex items-center ${
								active && "bg-primary-soft"
							} hover:bg-primary-soft cursor-default transition-colors duration-100 ease-in`}
						>
							{item.name}
						</div>
					)
				}
			/>
		);
	}

	// In print mode, remove height constraints to allow content to flow across pages
	const containerClassName = isPrintMode 
		? "bg-background" 
		: "bg-background min-h-screen";

	return (
		<MuiThemeProvider theme={muiTheme}>
		<AntdConfigProvider theme={antdTheme}>
		<div className={containerClassName} style={isPrintMode ? { height: 'auto', overflow: 'visible' } : undefined}>
			{/* Hide toaster in print mode */}
			{!isPrintMode && <Toaster position="top-center" />}
			<KBarProvider actions={actions} options={{ toggleShortcut: "$mod+Shift+P" }}>
				{/* Hide command bar in print mode */}
				{!isPrintMode && (
					<KBarPortal>
						<KBarPositioner className="fixed inset-0 backdrop-blur-[1px]">
							<KBarAnimator className="inset-0 p-4 pt-[15vh]">
								<div className="bg-accent-dark max-w-2xl mx-auto rounded-lg shadow-2xl relative flex flex-col">
									<div className="flex items-center text-lg font-medium border-b border-slate-500 ">
										<KBarSearch 
										/>
									</div>
									<RenderResults />
								</div>
							</KBarAnimator>
						</KBarPositioner>
					</KBarPortal>
				)}
				{/* Hide global loader in print mode to avoid it appearing in PDF */}
				{!isPrintMode && (
					<ClipLoader
						color="#0"
						size="50px"
						cssOverride={override}
						loading={isFetching > 0 || isMutating > 0}
					/>
				)}
				<Outlet
					context={{
						// jwt, setJWT,
						// toggleRefresh,
						selectedOrganisation,
						setSelectedOrganisation,
						selectedOrganisationLevel,
						setSelectedOrganisationLevel,
					}}
				/>
			</KBarProvider>
		</div>
		</AntdConfigProvider>
		</MuiThemeProvider>
	);
}
